import {
  CustomAttributeFieldType,
  isCustomAttributeFieldType,
} from '@risksmart-app/form-configuration/src/field-types/types';
import type { Control, FieldValues } from 'react-hook-form';
import type { FieldEditorValues } from 'src/blocks/form-editor-dialog/config';
import type { FieldSummary } from 'src/blocks/form-editor-dialog/constants';

import { CustomFieldProvider } from '../custom-attributes/context/CustomFieldProvider';
import { fieldTypesConfig } from '../custom-attributes/field-types';
import type { CustomAttributeProps } from '../custom-attributes/renderers/field-layouts/CustomAttributeProps';
import { Controller } from '../field-controller/Controller';

/**
 * Builds a minimal JSON schema fragment from the editor's field options,
 * matching the format expected by CustomAttribute FieldComponents.
 */
const buildSchemaForField = (
  fieldConfig: FieldEditorValues | undefined,
  editorType: string
) => {
  const options = fieldConfig?.options ?? [];

  if (
    (editorType === CustomAttributeFieldType.Select ||
      editorType === CustomAttributeFieldType.MultiSelect) &&
    options.length > 0
  ) {
    return {
      type: 'string' as const,
      oneOf: options.map((opt) => ({
        const: opt.id,
        title: opt.label,
      })),
    };
  }

  return { type: 'string' as const };
};

/**
 * Creates a fully functional react-hook-form controlled element for a new field
 * added in the form editor dialog. Follows the same pattern as
 * useControlledCustomAttributes — uses fieldTypesConfig[type].FieldComponent
 * wrapped in a Controller.
 *
 * Returns null if the field type cannot be mapped to a custom attribute type.
 *
 * `name` is the **editor id**, not the form-value key, and must stay that way:
 * the preview's config plane (`convertEditorStateToFormData`) is keyed the same
 * way, and `getRequiredFields` pairs the two by name. LiveFormPreview mirrors
 * the record's value onto the editor id so this binding still shows real data —
 * see the invariant comment on its seeding loop (CUS-365).
 */
export const createNewFieldElement = (
  fieldId: string,
  field: FieldSummary,
  fieldConfig: FieldEditorValues | undefined,
  control: Control<FieldValues>
): { key: string; value: React.ReactElement } | null => {
  if (!isCustomAttributeFieldType(field.type)) {
    return null;
  }
  const customAttributeType = field.type as CustomAttributeFieldType;

  const typeConfig = fieldTypesConfig[customAttributeType];
  if (!typeConfig) {
    return null;
  }

  const schema = buildSchemaForField(fieldConfig, field.type);
  const FieldComponent = typeConfig.FieldComponent;
  const options = fieldConfig?.options ?? [];

  const element = (
    <Controller
      key={fieldId}
      name={fieldId}
      control={control}
      defaultRequired={false}
      forceRequired={false}
      allowDefaultValue={false}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const props: CustomAttributeProps = {
          value: value ?? '',
          onChange,
          label: fieldConfig?.fieldName ?? field.name,
          disabled: field.readOnly,
          error: error?.message,
          description: fieldConfig?.guidanceEnabled
            ? fieldConfig.guidance
            : undefined,
          schema,
        };

        return (
          <CustomFieldProvider
            fieldPath={fieldId}
            currentField={{
              Label: props.label,
              Type: customAttributeType,
              ShowAltValues: false,
              Options: typeConfig.hasOptions
                ? options.map((opt) => ({
                    _tag: 'AltValueOption' as const,
                    AltValue: opt.id,
                    Value: opt.label,
                    GeneratedId: crypto.randomUUID(),
                  }))
                : undefined,
              Required: field.required,
              ReadOnly: field.readOnly ?? false,
              Hidden: false,
              Description: fieldConfig?.guidanceEnabled
                ? fieldConfig.guidance
                : undefined,
            }}
          >
            <FieldComponent {...props} />
          </CustomFieldProvider>
        );
      }}
    />
  );

  return { key: fieldId, value: element };
};
